import styled from "@emotion/styled";
import { LightMode } from "@repo/skapa";
import { Variables } from "@repo/skapa";
import { Text } from "@repo/skapa";
import { t } from "@repo/translation";
import { type DragEvent, useRef, useState } from "react";

interface DragAndDropProps {
  active: boolean;
}

const DragAndDropContainer = styled.div<DragAndDropProps>`
  border: ${Variables.thicknessThin} dashed ${LightMode.colourNeutral7};
  border-radius: ${Variables.radiusL};
  padding: ${Variables.space250};
  display: flex;
  justify-content: center;
  align-items: center;
  outline-width: ${Variables.thicknessThin};

  ${(props) => props.active && `background-color: ${LightMode.colourNeutral3};`}

  &:hover {
    background-color: ${LightMode.colourNeutral3};
    cursor: pointer;
  }
`;

const FileInput = styled.input`
  display: none;
`;

export const FileUpload = ({
  upload,
  accept,
}: {
  upload: (file: any) => Promise<void>;
  accept: string;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  // handle drag events
  const handleDrag = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // triggers when file is dropped
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      upload(e.dataTransfer.files);
    }
  };

  const onUpload = (file: any) => {
    setLoading(true);
    upload(file).then(() => setLoading(false));
  };
  return (
    <>
      <DragAndDropContainer
        className="saleslead__element--focus-visible"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            fileInputRef.current?.click();
          }
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        active={dragActive}
        tabIndex={0}
        data-testid="drag-and-drop-container"
      >
        <Text textStyle="Heading.XS">
          {loading ? t("prompt.uploading") : t("prompt.drag_to_upload")}
        </Text>
      </DragAndDropContainer>
      <FileInput
        type="file"
        id="file"
        ref={fileInputRef}
        name="file"
        data-testid="file"
        accept={accept}
        onChange={(event) => {
          onUpload(event.target.files);
        }}
        multiple
      />
    </>
  );
};
